if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      if (this.hasChildNodes()) {
        this.init();
      }
    }

    init() {
      this.form = this.querySelector('.js-product-form');

      if (!this.form) return;

      const variantInput =
        this.form.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.disabled = false;
      }

      this.cartDrawer =
        document.querySelector('cart-drawer');

      this.submitBtn =
        this.querySelector('[name="add"]');

      if (
        this.submitBtn
        && theme.settings.afterAtc !== 'no-js'
      ) {
        /*
         * init() 可能被 Quick Add 再次调用。
         * 使用固定的绑定函数，避免重复注册 submit。
         */
        this.boundHandleSubmit =
          this.boundHandleSubmit
          || this.handleSubmit.bind(this);

        this.removeEventListener(
          'submit',
          this.boundHandleSubmit
        );

        this.addEventListener(
          'submit',
          this.boundHandleSubmit
        );
      }
    }

    /**
     * Handles product form submission.
     *
     * @param {SubmitEvent} evt
     */
    async handleSubmit(evt) {
      if (
        evt.target.id
        === 'product-signup_form'
      ) {
        return;
      }

      evt.preventDefault();

      if (!this.submitBtn) return;

      /*
       * loading 阶段通过 aria-disabled 阻止重复提交。
       * 成功动画阶段通过 is-atc-success 阻止重复提交。
       */
      if (
        this.submitBtn.getAttribute(
          'aria-disabled'
        ) === 'true'
        || this.submitBtn.classList.contains(
          'is-atc-success'
        )
      ) {
        return;
      }

      if (
        theme.settings.vibrateOnATC
        && window.navigator.vibrate
      ) {
        window.navigator.vibrate(100);
      }

      const hasBurstAnimation =
        this.submitBtn.classList.contains(
          'js-atc-burst'
        );

      let addedSuccessfully = false;
      let successAnimation = null;

      this.errorMsg = null;
      this.setErrorMsgState();

      if (hasBurstAnimation) {
        this.setAtcBurstColor();
      }

      /*
       * 请求期间禁用按钮，
       * 并显示主题原有 loading Spinner。
       */
      this.submitBtn.setAttribute(
        'aria-disabled',
        'true'
      );

      this.submitBtn.classList.remove(
        'is-success'
      );

      this.submitBtn.classList.add(
        'is-loading'
      );

      const formData =
        new FormData(this.form);

      let sections =
        'cart-icon-bubble';

      if (this.cartDrawer) {
        const cartSection =
          this.cartDrawer.closest(
            '.shopify-section'
          );

        if (cartSection) {
          sections += `,${cartSection.id.replace(
            'shopify-section-',
            ''
          )}`;
        }
      }

      formData.append(
        'sections_url',
        window.location.pathname
      );

      formData.append(
        'sections',
        sections
      );

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept:
            'application/javascript',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        body: formData
      };

      try {
        /*
         * 添加商品前读取旧购物车，
         * 用于判断是新增商品还是修改数量。
         */
        const oldCartResponse =
          await fetch(
            `${theme.routes.cart}.js`
          );

        if (!oldCartResponse.ok) {
          throw new Error(
            `Unable to read cart: ${oldCartResponse.status}`
          );
        }

        const oldCartData =
          await oldCartResponse.json();

        /*
         * 添加商品。
         */
        const response =
          await fetch(
            theme.routes.cartAdd,
            fetchRequestOpts
          );

        const data =
          await response.json();

        if (data.status) {
          this.setErrorMsgState(
            data.description
          );
        }
        if (!response.ok) {
          throw new Error(
            data.description
            || `Unable to add item: ${response.status}`
          );
        }

        addedSuccessfully = true;

        /*
         * Shopify 确认成功后，
         * 才播放 Collection 成功动画。
         */
        if (hasBurstAnimation) {
          /*
           * 先添加成功动画 class，
           * 动画期间用它阻止重复点击。
           */
          this.submitBtn.classList.add(
            'is-atc-success'
          );

          /*
           * loading 请求已结束。
           */
          this.submitBtn.classList.remove(
            'is-loading'
          );

          /*
           * 立即移除 aria-disabled。
           *
           * 避免继续匹配：
           *
           * .btn[aria-disabled=true] {
           *   opacity: 0.4;
           * }
           */
          this.submitBtn.removeAttribute(
            'aria-disabled'
          );
          successAnimation =
            this.playAtcSuccessAnimation();
        }
        /*
         * 动画期间刷新完整购物车数据，
         * 并发送主题原有购物车事件。
         */
        const cartEventPromise =
          this.dispatchCartEvents(
            oldCartData,
            data
          );

        /*
         * 设置为跳转购物车页面时，
         * 等成功动画播放结束再跳转。
         */
        if (
          theme.settings.afterAtc
          === 'page'
        ) {
          await cartEventPromise;
          if (successAnimation) {
            await successAnimation;
          } else {
            await ProductForm.wait(300);
          }

          window.location.href =
            theme.routes.cart;

          return;
        }

        /*
         * 更新页头购物车数量。
         */
        ProductForm.updateCartIcon(data);

        /*
         * Quick Add drawer
         * 保留主题原有成功提示。
         */
        const quickAddDrawer =
          this.closest(
            'quick-add-drawer'
          );

        if (quickAddDrawer) {
          quickAddDrawer.addedToCart();
        }

        const shouldOpenCartDrawer =
          Boolean(this.cartDrawer)
          && !quickAddDrawer
          && theme.settings.afterAtc
          === 'drawer';

        /*
         * Collection 动画按钮：
         * 等动画完全结束再打开购物车抽屉。
         */
        if (
          shouldOpenCartDrawer
          && successAnimation
        ) {
          await successAnimation;

          this.cartDrawer.renderContents(
            data,
            true
          );
        } else {
          /*
           * 不需要等待动画打开抽屉时，
           * 保留主题原有延迟更新。
           */
          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                shouldOpenCartDrawer
              );
            } else if (
              window.location.pathname
              === theme.routes.cart
            ) {
              const cartItems =
                document.querySelector(
                  'cart-items'
                );

              if (cartItems) {
                cartItems.refreshCart();
              }
            }
          }, 700);
        }

        if (successAnimation) {
          await successAnimation;
        }
        await cartEventPromise;
      } catch (error) {
        console.error(error);

        /*
         * cart/add 本身没有成功时，
         * 才发送加入购物车错误事件。
         */
        this.dispatchEvent(
          new CustomEvent(
            'on:cart:error',
            {
              bubbles: true,
              detail: {
                error:
                  this.errorMsg
                    ? this.errorMsg
                      .textContent
                    : error.message
              }
            }
          )
        );
      } finally {
        if (hasBurstAnimation) {
          /*
           * 成功或失败后恢复动画按钮。
           */
          this.resetAtcSuccessAnimation();

          this.submitBtn.classList.remove(
            'is-loading',
            'is-success'
          );

          this.submitBtn.removeAttribute(
            'aria-disabled'
          );
        } else if (addedSuccessfully) {
          /*
           * 非动画按钮保持主题原有成功状态。
           */
          this.submitBtn.classList.add(
            'is-success'
          );

          this.submitBtn.removeAttribute(
            'aria-disabled'
          );

          setTimeout(() => {
            this.submitBtn.classList.remove(
              'is-loading',
              'is-success'
            );
          }, 1400);
        } else {
          /*
           * 请求失败时立即恢复按钮。
           */
          this.submitBtn.classList.remove(
            'is-loading',
            'is-success'
          );

          this.submitBtn.removeAttribute(
            'aria-disabled'
          );
        }
      }
    }

    /**
     * Fetches the latest cart and dispatches
     * the theme cart event.
     *
     * @param {object} oldCartData
     * @param {object} data
     */
    async dispatchCartEvents(
      oldCartData,
      data
    ) {
      try {
        const newCartResponse =
          await fetch(
            `${theme.routes.cart}.js`
          );

        if (!newCartResponse.ok) {
          throw new Error(
            `Unable to refresh cart: ${newCartResponse.status}`
          );
        }

        const newCartData =
          await newCartResponse.json();

        const itemInOldCart =
          oldCartData.items.find(
            (item) =>
              item.variant_id
              === data.variant_id
          );

        if (itemInOldCart) {
          this.dispatchEvent(
            new CustomEvent(
              'on:line-item:change',
              {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  variantId:
                    data.variant_id,
                  oldQuantity:
                    itemInOldCart.quantity,
                  newQuantity:
                    itemInOldCart.quantity
                      === data.quantity
                      ? itemInOldCart
                        .quantity
                      : data.quantity
                }
              }
            )
          );
        } else {
          this.dispatchEvent(
            new CustomEvent(
              'on:cart:add',
              {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  variantId:
                    data.variant_id
                }
              }
            )
          );
        }
      } catch (error) {
        console.error(error);
        this.dispatchEvent(
          new CustomEvent(
            'on:cart:error',
            {
              bubbles: true,
              detail: {
                error:
                  this.errorMsg
                    ? this.errorMsg
                      .textContent
                    : error.message
              }
            }
          )
        );
      }
    }

    /**
     * 使用按钮当前前景色作为成功图形颜色。
     * 与 CodePen 原版的 currentColor 行为一致。
     */
    setAtcBurstColor() {
      if (!this.submitBtn) return;

      const styles =
        window.getComputedStyle(
          this.submitBtn
        );

      const foregroundColor =
        styles.color || '#ffffff';

      this.submitBtn.style.setProperty(
        '--atc-check-color',
        foregroundColor
      );

      this.submitBtn.style.setProperty(
        '--atc-burst-color',
        foregroundColor
      );
    }

    /**
     * 播放从 CodePen emJdWYE 提取的成功动画。
     *
     * 动画本身由 CSS Transition 驱动：
     * 1. 显示成功层；
     * 2. 切换 data-atc-adding="true"；
     * 3. 等待子元素动画结束；
     * 4. 保留成功状态 500ms。
     *
     * @returns {Promise<void>}
     */
    async playAtcSuccessAnimation() {
      if (!this.submitBtn) return;

      const defaultContent =
        this.submitBtn.querySelector(
          '.js-atc-default'
        );

      const successLayer =
        this.submitBtn.querySelector(
          '.js-atc-success'
        );

      const checkGroup =
        this.submitBtn.querySelector(
          '.js-atc-check-group'
        );

      const ring =
        this.submitBtn.querySelector(
          '.js-atc-ring'
        );

      const check =
        this.submitBtn.querySelector(
          '.js-atc-check'
        );

      const rays =
        Array.from(
          this.submitBtn.querySelectorAll(
            '.js-atc-ray'
          )
        );

      if (
        !defaultContent
        || !successLayer
        || !checkGroup
        || !ring
        || !check
        || rays.length === 0
      ) {
        return;
      }

      const reduceMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      /*
       * 清掉上一次可能残留的状态。
       * reset 是同步的，随后立即重新进入成功状态。
       */
      this.resetAtcSuccessAnimation();

      this.submitBtn.classList.add(
        'is-atc-success'
      );

      /*
       * 先让浏览器提交“成功层已显示”的初始帧，
       * 再切换 data 属性，确保 Transition 会从初始态播放。
       */
      await ProductForm.nextFrame();

      this.submitBtn.dataset.atcAdding =
        'true';

      if (reduceMotion) {
        await ProductForm.wait(700);
        return;
      }

      /*
       * 等一帧，让 CSS Transition 被浏览器实例化。
       */
      await ProductForm.nextFrame();

      const animatedElements = [
        defaultContent,
        checkGroup,
        ring,
        check,
        ...rays
      ];

      const animations =
        animatedElements.flatMap(
          (element) => {
            if (
              typeof element.getAnimations
              !== 'function'
            ) {
              return [];
            }

            return element.getAnimations();
          }
        );

      if (animations.length > 0) {
        const animationFinished =
          Promise.allSettled(
            animations.map(
              (animation) =>
                animation.finished
            )
          );

        /*
         * 防止浏览器取消 Transition 后 finished
         * 一直不返回；正常情况下原版约 1.14s 完成。
         */
        await Promise.race([
          animationFinished,
          ProductForm.wait(1600)
        ]);
      } else {
        /*
         * 不支持 Web Animations API 时，
         * 按原版最长时间做兼容等待。
         */
        await ProductForm.wait(1150);
      }

      /*
       * 对应原 Pen 动画结束后的 500ms 成功停留。
       */
      await ProductForm.wait(500);
    }

    /**
     * 清理成功动画状态。
     * CSS 在 data 属性移除后会立即回到初始图形，
     * 默认按钮内容则按原版 transition 展开恢复。
     */
    resetAtcSuccessAnimation() {
      if (!this.submitBtn) return;

      this.submitBtn.removeAttribute(
        'data-atc-adding'
      );

      this.submitBtn.classList.remove(
        'is-atc-success'
      );
    }

    /**
     * 更新页头购物车图标。
     *
     * @param {object} response
     */
    static updateCartIcon(
      response
    ) {
      const cartIconBubble =
        document.getElementById(
          'cart-icon-bubble'
        );

      if (
        cartIconBubble
        && response.sections
        && response.sections[
        'cart-icon-bubble'
        ]
      ) {
        cartIconBubble.innerHTML =
          response.sections[
          'cart-icon-bubble'
          ];
      }
    }

    /**
     * 等待浏览器绘制下一帧。
     *
     * @returns {Promise<void>}
     */
    static nextFrame() {
      return new Promise(
        (resolve) => {
          let resolved = false;

          const finish = () => {
            if (resolved) return;

            resolved = true;
            resolve();
          };

          window.requestAnimationFrame(
            finish
          );

          /*
           * 浏览器标签页处于后台时，
           * requestAnimationFrame 可能被长时间暂停。
           */
          window.setTimeout(
            finish,
            50
          );
        }
      );
    }

    /**
     * Promise 延迟工具。
     *
     * @param {number} duration
     * @returns {Promise<void>}
     */
    static wait(duration) {
      return new Promise(
        (resolve) => {
          setTimeout(
            resolve,
            duration
          );
        }
      );
    }

    /**
     * 显示或隐藏表单错误信息。
     *
     * @param {string|boolean} error
     */
    setErrorMsgState(
      error = false
    ) {
      this.errorMsg =
        this.errorMsg
        || this.querySelector(
          '.js-form-error'
        );

      if (!this.errorMsg) return;

      this.errorMsg.hidden =
        !error;

      if (error) {
        this.errorMsg.textContent =
          error;
      }
    }
  }

  customElements.define(
    'product-form',
    ProductForm
  );
}